import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { FlatList, ScrollView, SectionList, TextInput, TouchableOpacity, View } from 'react-native'

import api from "../../../../../utils/api"
import tw from '../../../../../styles/tailwind';
import Text from '../../../../../components/Text';
import IcomoonIcon from '../../../../../components/IcomoonIcon';
import { Icon } from '../../../../../components/Icon';
import { Illustration } from '../../../../../components/Illustration';
import { Image } from 'expo-image';
import { useQuery } from '@tanstack/react-query';
import { useNavigation, useRoute } from '@react-navigation/native';
import IconButton from '../../../../../components/IconButton';
import AddPaymentModal from '../Fees/AddPaymentModal';
import { ProgressBar, useProgressPercentAnimation } from '../Fees/FeeDetailScreen';

import CreateFeeModal from '../Fees/CreateFeeModal';
import { pushDetail } from '../../../../../navigation/MasterDetailNavigator';
import TabsSelector from '../../../../../components/TabsSelector';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';
import useOpenDetailParam from '@/hooks/useOpenDetailParam';



function FeeButton({ fee, style = {} }) {
  const navigation = useNavigation()
  const { communityId } = useActiveAdminCommunity()
  const selected = useOpenDetailParam(navigation, 'Admin.Fees.Detail', 'feeId') === fee.uuid

  function goToFeeDetail() {
    navigation.navigate("Admin.Fees.Detail", {
      feeId: fee.uuid,
      communityId,
    })
  }

  return (
    <TouchableOpacity
      style={tw.style(`bg-white rounded-2xl border border-gray-200 px-5 py-4`, selected && `bg-selected border-selected-border`, style)}
      onPress={goToFeeDetail}
    >
      {/* Título */}
      <View style={tw`row items-center mb-3`}>
        <View style={tw`w-2.5 h-2.5 rounded-full bg-primary mr-2`} />
        <Text style={tw`flex-1 text-secondary font-700 text-base`}>{fee.name}</Text>
      </View>

      {/* Etiqueta (catálogo de cuotas — por ahora muestra el tipo) */}
      {
        fee.type?.name &&
          <View style={tw`self-start bg-primary-50 border border-primary/30 rounded-full px-3 py-1 mb-4`}>
            <Text style={tw`text-primary-600 font-500`}>{fee.type.name}</Text>
          </View>
      }

      {/* Barra */}
      <ProgressBar
        progressPercent={fee.charged_units_percent}
        containerStyle={tw`border-0 bg-primary/15 h-2.5 rounded mb-4`}
        barStyle={tw`bg-primary h-2.5 rounded w-${fee.charged_units_percent}%`}
      />

      {/* Métricas */}
      <View style={tw`row justify-between items-end`}>
        <View style={tw`items-start`}>
          <Text style={tw`text-gray-400 text-sm mb-0.5`}>Participación</Text>
          <Text style={tw`text-secondary font-700 text-base`}>{fee.charged_units_percent}%</Text>
        </View>

        <View style={tw`items-center`}>
          <Text style={tw`text-gray-400 text-sm mb-0.5`}>Cobrado</Text>
          <Text style={tw`text-primary-600 font-700 text-base`}>${fee.charged_amount?.toLocaleString()}</Text>
        </View>

        <View style={tw`items-end`}>
          <Text style={tw`text-gray-400 text-sm mb-0.5`}>Restante</Text>
          <Text style={tw`text-secondary font-700 text-base`}>${fee.pending_amount?.toLocaleString()}</Text>
        </View>
      </View>
    </TouchableOpacity>
  )
}



function MonthFeeCard({ fee, advance = false }) {
  const navigation = useNavigation()
  const { communityId } = useActiveAdminCommunity()
  const selected = useOpenDetailParam(navigation, 'Admin.Fees.Detail', 'feeId') === fee.uuid

  const now = new Date()
  const currentYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const isCurrentMonth = !advance && fee.apply_month?.slice(0, 7) === currentYM

  function goToFeeDetail() {
    navigation.navigate("Admin.Fees.Detail", { feeId: fee.uuid, communityId })
  }

  return (
    <TouchableOpacity
      onPress={goToFeeDetail}
      style={tw.style(
        `bg-white rounded-2xl border px-4 py-3 mb-3`,
        isCurrentMonth ? `border-secondary-300` : `border-gray-200`,
        selected && `bg-selected border-selected-border`
      )}
    >
      <View style={tw`row justify-between items-center mb-2`}>
        <View style={tw`row items-center`}>
          <Text style={tw`text-secondary font-500 text-base`}>{fee.name}</Text>
          {
            isCurrentMonth &&
              <View style={tw`bg-secondary-300 rounded-full px-2 py-0.5 ml-2`}>
                <Text style={tw`text-white text-xs font-600`}>actual</Text>
              </View>
          }
        </View>
        <Text style={tw`text-secondary-300 font-700 text-lg`}>{fee.charged_units_percent}%</Text>
      </View>

      <ProgressBar
        progressPercent={fee.charged_units_percent}
        containerStyle={tw`border-0 bg-secondary-50 h-2.5 rounded`}
        barStyle={tw`bg-secondary-300 h-2.5 rounded w-${fee.charged_units_percent}%`}
      />

      <View style={tw`row justify-between items-center mt-2`}>
        {
          advance ?
            <Text style={tw`text-gray-400`}>Adelantado <Text style={tw`text-secondary font-600`}>${fee.charged_amount?.toLocaleString()}</Text></Text>
          :
            <View style={tw`row items-center`}>
              <Text style={tw`text-gray-400`}>Cobrado <Text style={tw`text-secondary font-600`}>${fee.charged_amount?.toLocaleString()}</Text></Text>
              <Text style={tw`text-gray-300 mx-2`}>·</Text>
              <Text style={tw`text-gray-400`}>Restante <Text style={tw`text-secondary font-600`}>${fee.pending_amount?.toLocaleString()}</Text></Text>
            </View>
        }
        <IcomoonIcon name='chevron-right' size={13} color={tw.color('gray-400')} />
      </View>
    </TouchableOpacity>
  )
}



// Lista de años en acordeón (reutilizada por el historial y por los adelantados).
// Maneja su propio estado de expansión; expande el primer grupo por defecto.
function YearAccordionList({ groups, advance = false }) {
  const [expandedYears, setExpandedYears] = useState({})
  function toggleYear(year) {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }))
  }
  useEffect(() => {
    if (groups.length > 0) {
      setExpandedYears(prev => (Object.keys(prev).length === 0 ? { [groups[0].year]: true } : prev))
    }
  }, [groups])

  return (
    <>
      {
        groups.map(({ year, fees, count, avgPercent }) => {
          const expanded = !!expandedYears[year]
          return (
            <View key={year} style={tw`mx-3`}>
              <TouchableOpacity
                onPress={() => toggleYear(year)}
                style={tw.style(
                  `row justify-between items-center`,
                  expanded ? `px-1 py-3 mb-1` : `bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`
                )}
              >
                <Text style={tw`text-gray-800 font-700 text-2xl`}>{year}</Text>

                <View style={tw`row items-center`}>
                  <Text style={tw`text-gray-400 mr-2`}>{count} periodos · {avgPercent}%</Text>
                  <IcomoonIcon
                    name='chevron-right'
                    size={13}
                    color={tw.color('gray-400')}
                    style={{ transform: [{ rotate: expanded ? '90deg' : '0deg' }] }}
                  />
                </View>
              </TouchableOpacity>

              {
                expanded && fees.map(fee => <MonthFeeCard key={fee.uuid} fee={fee} advance={advance} />)
              }
            </View>
          )
        })
      }
    </>
  )
}



const FeesScreen = forwardRef(function ({ filter, subtabs }, ref) {
  const navigation = useNavigation()
  const route = useRoute()
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showCreateFeeModal, setShowCreateFeeModal] = useState(false);

  const {communityId} = useActiveAdminCommunity()

  const [typeFilter, setTypeFilter] = useState(filter ?? subtabs?.[0]?.value ?? "mainteinance");


  const { isInitialLoading, error, data: feesOverview, refetch: refetchFees } = useQuery({
    queryKey: ['community.fees.overview', communityId],
    queryFn: () => api.get(`/communities/${communityId}/fees/overview`).then(response => response.data),
  })



  useEffect(() => {
    navigation.addListener('focus', () => {
      refetchFees()
    })
  }, []);

  const categorizedFees = useMemo(() => {
    const last_monthly = feesOverview?.fees?.find(e => e.type.type === "main")
    const last_extra = feesOverview?.fees?.find(e => e.type.type !== "main")
    const past = feesOverview?.fees?.filter(e => new Date(e.apply_month) <= new Date() && e.type.type === "main") //  && e.uuid !== last_extra?.uuid
    const future = feesOverview?.fees?.filter(e => new Date(e.apply_month) > new Date()).sort((a, b) => new Date(a.apply_month) > new Date(b.apply_month))
    const extra = feesOverview?.fees?.filter(e => e.type.type !== "main")


    return {
      past,
      future,
      extra,
      last_extra,
      last_monthly,

      all: feesOverview?.fees,
    }
  }, [feesOverview])

  // Agrupar cuotas de mantenimiento por año (acordeón anual).
  const maintenanceByYear = useMemo(() => {
    const groups = {}
    categorizedFees?.past?.forEach(fee => {
      const year = fee.apply_month?.slice(0, 4) || '—'
      if (!groups[year]) groups[year] = []
      groups[year].push(fee)
    })
    return Object.keys(groups)
      .sort((a, b) => Number(b) - Number(a))
      .map(year => {
        const fees = groups[year].slice().sort((a, b) => (a.apply_month < b.apply_month ? 1 : -1))
        const avg = Math.round(fees.reduce((s, f) => s + (f.charged_units_percent || 0), 0) / fees.length)
        return { year, fees, count: fees.length, avgPercent: avg }
      })
  }, [categorizedFees])

  // Cuotas de mantenimiento ADELANTADAS (meses futuros), agrupadas por año.
  // Solo "main" (una extraordinaria futura, si existe, se queda en Cuotas).
  // Orden ascendente: el año/mes más próximo primero.
  const advanceByYear = useMemo(() => {
    const groups = {}
    categorizedFees?.future?.forEach(fee => {
      if (fee.type?.type !== "main") return
      const year = fee.apply_month?.slice(0, 4) || '—'
      if (!groups[year]) groups[year] = []
      groups[year].push(fee)
    })
    return Object.keys(groups)
      .sort((a, b) => Number(a) - Number(b))
      .map(year => {
        const fees = groups[year].slice().sort((a, b) => (a.apply_month > b.apply_month ? 1 : -1))
        const avg = Math.round(fees.reduce((s, f) => s + (f.charged_units_percent || 0), 0) / fees.length)
        return { year, fees, count: fees.length, avgPercent: avg }
      })
  }, [categorizedFees])

  // Toggle entre "cuota actual" (historial) y "cuotas adelantadas" en el tab Mantenimiento.
  const [showAdvance, setShowAdvance] = useState(false)

  useImperativeHandle(ref, function getRefValue() {
    return {
      // new ref value...
      setShowCreateFeeModal(value) { setShowCreateFeeModal(value) },
    }
  }, []) // dependencies



  return (
    <>
      <ScrollView style={tw`flex-1 mb-10`} contentContainerStyle={tw.style(!subtabs && `pt-5`)}>
        {
          subtabs &&
            <View style={tw`mb-5`}>
              <TabsSelector
                options={subtabs}
                onChange={option => setTypeFilter(option)}
              />
            </View>
        }

        {/* {
          isInitialLoading &&
            <Text style={tw`mt-20 text-gray-300 text-center`}>Cargando...</Text>
        } */}

        {
          feesOverview?.fees?.length === 0 &&
          <Text style={tw`mt-20 text-gray-400 text-center`}>Sin cuotas registradas</Text>
        }


        {/* {

          categorizedFees?.last_extra &&
            <FeeButton fee={categorizedFees?.last_extra} style={tw`mx-3 mb-5`} />
        } */}

        {/* {
          ! isInitialLoading &&
            <IconButton
              title={"Crear cuota extraordinaria"}
              icon='add1'
              iconSize={18}
              color='transparent'
              textColor='primary'
              style={tw`mx-3 mb-4 py-3 border-[0.4] border-primary border-dashed`}
              onPress={() => setShowCreateFeeModal(true)}
            />
        } */}

        {
          isInitialLoading &&
          <Text style={tw`text-lg text-gray-600 mt-10 text-center`}>Cargando...</Text>
        }

        {/* {
          categorizedFees?.last_monthly &&
          <FeeButton fee={categorizedFees?.last_monthly} style={tw`bg-white mx-3 mb-5`} />
        } */}

        {
          (typeFilter == "mainteinance") && (
            showAdvance ?
              <>
                {/* Vista de cuotas adelantadas (meses futuros) */}
                <TouchableOpacity
                  onPress={() => setShowAdvance(false)}
                  style={tw`mx-3 mb-3 row items-center px-1 py-2`}
                >
                  <IcomoonIcon name='chevron-left' size={14} color={tw.color('secondary')} />
                  <Text style={tw`text-secondary font-500 ml-2`}>Regresar a cuota actual</Text>
                </TouchableOpacity>

                {
                  advanceByYear.length === 0 ?
                    <Text style={tw`mt-10 text-gray-400 text-center`}>Sin cuotas adelantadas</Text>
                  :
                    <YearAccordionList groups={advanceByYear} advance />
                }
              </>
            :
              <>
                {/* Entrada a cuotas adelantadas */}
                <TouchableOpacity
                  onPress={() => setShowAdvance(true)}
                  style={tw`mx-3 mb-3 row justify-between items-center bg-white rounded-2xl border border-gray-200 px-4 py-3`}
                >
                  <View style={tw`row items-center`}>
                    <Icon name="calendario" variant="gris" width={18} height={20} />
                    <Text style={tw`text-secondary font-500 ml-2`}>Ver cuotas adelantadas</Text>
                  </View>
                  <IcomoonIcon name='chevron-right' size={13} color={tw.color('secondary')} />
                </TouchableOpacity>

                <YearAccordionList groups={maintenanceByYear} />
              </>
          )
        }

        {
          (typeFilter == "other") && categorizedFees?.extra?.map(fee =>
            <FeeButton key={fee.uuid} fee={fee} style={tw`mx-3 mb-4`} />
          )
        }

        {/* Cuotas anteriores */}
        {/* <View style={tw`mt-4 gap-y-5 px-3 py-4 rounded-t-3xl bg-gray-100 flex-1 pb-20`}>
          <View style={tw`row px-2`}>
            <Image source={require('../../../../../assets/images/svgs/timeback.svg')} style={tw`h-7 w-6`} contentFit='contain' />

            <Text style={tw`text-base text-secondary ml-3 font-600`}>Cuotas anteriores</Text>
          </View>

          {
            isInitialLoading &&
            <Text style={tw`text-lg text-gray-600 mt-10 text-center`}>Cargando...</Text>
          }
          
          {
            categorizedFees?.past?.map(fee => 
              <FeeButton isPast key={fee.uuid} fee={fee} style={tw`bg-white`} />
            )
          }
        </View> */}
      </ScrollView>



      <IconButton
        title={"Registrar Pago"}
        color='primary'
        textColor='white'
        style={tw`absolute left-0 right-0 rounded-b-0 rounded-t-2xl bottom-0 self-center`}
        onPress={() => setShowAddPaymentModal(true)}
      />

      <AddPaymentModal
        communityId={communityId}
        onRequestClose={() => setShowAddPaymentModal(false)}
        visible={showAddPaymentModal}
        onPaymentCreated={() => {
          setShowAddPaymentModal(false)
          refetchFees()
        }}
      />

      <CreateFeeModal
        communityId={communityId}
        onRequestClose={() => setShowCreateFeeModal(false)}
        visible={showCreateFeeModal}
        onFeeCreated={(feeResponse) => {
          setShowCreateFeeModal(false)
          refetchFees()

          navigation.navigate("Admin.Fees.Detail", {
            feeId: feeResponse.fee.uuid,
            communityId: communityId,
          })
        }}
      />
    </>
  )
})

export default FeesScreen